import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, RefreshCw, Power, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDialog, Field, PageHeader, Panel, RowsSkeleton, StatusBadge, EmptyState, Tag } from "@/components/app/kit";
import { ModuleSelector } from "@/components/platform/ModuleSelector";
import { InviteNotice } from "@/pages/platform/TenantNew";
import { DomainRow } from "@/pages/platform/Domains";
import { useFetch } from "@/hooks/useServerList";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/i18n";

const Info = ({ label, value, mono }) => (
  <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p><p className={`text-sm text-slate-800 mt-1 ${mono ? "font-mono" : ""}`}>{value || "—"}</p></div>
);

const Overview = ({ t, stats }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <Panel title="Identité et coordonnées">
      <div className="grid grid-cols-2 gap-5">
        <Info label="Nom commercial" value={t.commercial_name} /><Info label="Type" value={t.establishment_type} />
        <Info label="Spécialités" value={(t.specialties || []).join(", ")} /><Info label="Adresse" value={`${t.address}, ${t.postal_code} ${t.city} (${t.country_code})`} />
        <Info label="Téléphone" value={t.phone} /><Info label="E-mail" value={t.email} />
        <Info label="Responsable légal" value={t.legal_representative?.full_name} /><Info label="Responsable médical" value={t.medical_director?.full_name} />
        <Info label="Immatriculation" value={t.registration_number} mono /><Info label="Identifiant fiscal" value={t.tax_id} mono />
      </div>
    </Panel>
    <Panel title="Abonnement et paramètres">
      <div className="grid grid-cols-2 gap-5">
        <Info label="Formule" value={t.plan_code} /><Info label="Période" value={`${fmtDate(t.start_date)} → ${fmtDate(t.end_date)}`} />
        <Info label="Utilisateurs" value={`${stats.users} / ${t.limits?.max_users}`} /><Info label="Patients" value={`${stats.patients} / ${t.limits?.max_patients ?? "illimité"}`} />
        <Info label="Stockage" value={`${t.limits?.storage_mb} Mo`} /><Info label="Devise · Langue" value={`${t.currency} · ${t.language}`} />
        <Info label="Fuseau horaire" value={t.timezone} /><Info label="Locale" value={t.locale} />
        <Info label="Identifiant technique" value={t.slug} mono /><Info label="Créé le" value={fmtDateTime(t.created_at)} />
      </div>
    </Panel>
  </div>
);

const ModulesTab = ({ id }) => {
  const { data, reload } = useFetch(`/platform/tenants/${id}/modules`);
  const [value, setValue] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setValue(data.modules.filter((m) => m.enabled).map((m) => m.code));
  }, [data]);
  if (!data || !value) return <RowsSkeleton />;
  const before = new Set(data.modules.filter((m) => m.enabled).map((m) => m.code));
  const removed = data.modules.filter((m) => before.has(m.code) && !value.includes(m.code));
  const added = value.filter((c) => !before.has(c));
  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.put(`/platform/tenants/${id}/modules`, { modules: value, reason });
      toast.success(`${r.changes.length} modification(s) enregistrée(s)`);
      setConfirm(false);
      setReason("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6">
      <Panel title="Modules autorisés" actions={
        <Button disabled={!removed.length && !added.length} onClick={() => (removed.length ? setConfirm(true) : save())} data-testid="tenant-modules-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer les modules
        </Button>}>
        <ModuleSelector modules={data.modules} value={value} onChange={setValue} planModules={data.plan_modules} />
      </Panel>
      <Panel title="Historique des changements">
        {data.history.length === 0 ? <p className="text-sm text-slate-500">Aucun changement.</p> : (
          <ul className="divide-y divide-slate-100 -my-2" data-testid="module-history">
            {data.history.map((h) => (
              <li key={h.id} className="py-2.5 flex flex-wrap items-center gap-3 text-sm">
                <Tag tone={h.action === "activation" ? "green" : "red"}>{h.action === "activation" ? "Activation" : "Désactivation"}</Tag>
                <span className="font-medium">{h.module_name}</span>
                <span className="text-slate-500">{h.action === "activation" ? "activé" : "désactivé"} par {h.actor_email}</span>
                {h.reason && <span className="text-slate-400 italic">« {h.reason} »</span>}
                <span className="ml-auto text-xs text-slate-400">{fmtDateTime(h.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} destructive busy={busy} confirmLabel="Confirmer la désactivation" onConfirm={save}
        title="Désactiver des modules ?" description="Les utilisateurs du cabinet perdront immédiatement l'accès à ces modules (menus, pages et API). Les données sont conservées et redeviendront accessibles en cas de réactivation.">
        <div className="space-y-3">
          <ul className="text-sm space-y-1">{removed.map((m) => <li key={m.code} className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-4 w-4" />{m.name}</li>)}</ul>
          <Field label="Motif (journalisé)"><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="modules-reason-input" /></Field>
        </div>
      </ConfirmDialog>
    </div>
  );
};

const StatusAction = ({ t, onDone }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(t.status);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${t.id}/status`, { status, reason });
      toast.success("Statut mis à jour");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="tenant-status-button"><Power className="h-4 w-4 mr-2" />Changer le statut</Button>
      <ConfirmDialog open={open} onOpenChange={setOpen} busy={busy} onConfirm={submit} destructive={status === "suspended"} title="Changer le statut du cabinet"
        description="Un cabinet suspendu ou expiré n'est plus accessible par ses utilisateurs. Ses données sont conservées.">
        <div className="space-y-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="tenant-status-select-dialog"><SelectValue /></SelectTrigger>
            <SelectContent>{[["active", "Actif"], ["trial", "Essai"], ["suspended", "Suspendu"], ["expired", "Expiré"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Field label="Motif"><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="tenant-status-reason" /></Field>
        </div>
      </ConfirmDialog>
    </>
  );
};

const Backups = ({ id, slug }) => {
  const { data, reload, error } = useFetch(`/platform/tenants/${id}/backups`);
  const [restore, setRestore] = useState(null);
  const [confirm, setConfirm] = useState("");
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const download = async (b) => { const r = await api.get(`/platform/backups/${b.id}/download`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `sauvegarde-${slug}-${b.id}.json.gz${b.encrypted ? ".enc" : ""}`; a.click(); };
  const KIND = { manual: "manuelle", auto: "automatique", pre_restore: "avant restauration" };
  if (error) return <EmptyState title="Réservé aux super administrateurs" text={error} />;
  return (
    <Panel title="Sauvegardes du cabinet" actions={<Button size="sm" onClick={() => run(() => api.post(`/platform/tenants/${id}/backups`), "Sauvegarde créée")} data-testid="backup-create">Créer une sauvegarde</Button>}>
      <p className="text-xs text-slate-500 mb-4">Instantané compressé et chiffré (AES, empreinte SHA-256) de toutes les données et pièces jointes du cabinet. « Tester » restaure la sauvegarde dans une base isolée temporaire et contrôle son intégrité sans toucher aux données réelles. La restauration remplace les données actuelles, après une sauvegarde de sécurité automatique.</p>
      {(data || []).map((b) => (
        <div key={b.id} className="flex flex-wrap items-center gap-3 py-2 border-b border-slate-100 last:border-0 text-sm" data-testid="backup-row">
          <span>{fmtDateTime(b.created_at)}</span><span className="text-xs text-slate-500">{Math.round(b.size / 1024)} Ko · {b.counts.patients || 0} patients · {KIND[b.kind] || "manuelle"} · par {b.created_by}</span>
          {b.encrypted ? <Tag tone="emerald">Chiffrée</Tag> : <Tag tone="amber">Non chiffrée (ancienne)</Tag>}
          {b.last_verification && <Tag tone={b.last_verification.ok ? "emerald" : "red"}>{b.last_verification.ok ? `Restauration testée le ${fmtDateTime(b.last_verification.checked_at)}` : "Test de restauration en échec"}</Tag>}
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => run(() => api.post(`/platform/backups/${b.id}/verify`), "Test de restauration terminé")} data-testid="backup-verify">Tester</Button>
          <Button size="sm" variant="outline" onClick={() => download(b)} data-testid="backup-download">Télécharger</Button>
          <Button size="sm" variant="outline" onClick={() => setRestore(b)} data-testid="backup-restore">Restaurer</Button>
        </div>))}
      {data && !data.length && <p className="text-sm text-slate-500">Aucune sauvegarde.</p>}
      <ConfirmDialog open={!!restore} onOpenChange={(o) => !o && setRestore(null)} destructive confirmLabel="Restaurer" title="Restaurer cette sauvegarde ?" disabled={confirm !== slug}
        description="Les données actuelles du cabinet seront remplacées. Saisissez l'identifiant technique pour confirmer."
        onConfirm={() => run(async () => { await api.post(`/platform/backups/${restore.id}/restore`, { confirm_slug: confirm }); setRestore(null); }, "Sauvegarde restaurée")}>
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={slug} data-testid="backup-confirm-slug" />
      </ConfirmDialog>
    </Panel>
  );
};

const Pharmacy = ({ id, t, onDone }) => {
  const cur = t.pharmacy_authorization || { authorized: false };
  const [ref, setRef] = useState(cur.reference || "");
  const save = async (authorized) => { try { await api.put(`/platform/tenants/${id}/pharmacy`, { authorized, reference: ref }); toast.success("Autorisation mise à jour"); onDone(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <Panel title="Activité pharmaceutique" testid="pharmacy-panel">
      <p className="text-sm text-slate-600 mb-3">Par défaut, le cabinet ne gère qu'un stock interne : la vente de médicaments au public est bloquée. N'activez cette option que sur présentation d'une autorisation d'exercice pharmaceutique valide.</p>
      <p className="text-sm mb-3" data-testid="pharmacy-current">Statut : <b>{cur.authorized ? `autorisée (réf. ${cur.reference})` : "non autorisée"}</b></p>
      <div className="flex flex-wrap gap-2"><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Référence de l'autorisation" className="max-w-sm" data-testid="pharmacy-reference" />
        {cur.authorized ? <Button variant="outline" onClick={() => save(false)} data-testid="pharmacy-revoke">Révoquer</Button> : <Button onClick={() => save(true)} data-testid="pharmacy-authorize">Enregistrer l'autorisation</Button>}</div>
    </Panel>
  );
};

export default function TenantDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useFetch(`/platform/tenants/${id}`);
  const [invite, setInvite] = useState(null);
  if (error) return <EmptyState title="Cabinet introuvable" text={error} />;
  if (loading || !data) return <RowsSkeleton rows={8} />;
  const t = data.tenant;
  const regenerate = async () => {
    try {
      setInvite((await api.post(`/platform/tenants/${id}/invite-admin`)).data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const pendingAdmin = data.members.some((m) => m.status === "invited");
  return (
    <div>
      <PageHeader eyebrow={`Cabinet · ${t.slug}`} title={<span className="flex items-center gap-3 flex-wrap">{t.name}<StatusBadge status={t.status} testid="tenant-detail-status" /></span>}
        subtitle={t.status_reason ? `Motif : ${t.status_reason}` : undefined}
        actions={<>
          {pendingAdmin && <Button variant="outline" onClick={regenerate} data-testid="tenant-regenerate-invite"><RefreshCw className="h-4 w-4 mr-2" />Régénérer l'invitation</Button>}
          <StatusAction t={t} onDone={reload} />
          <Button asChild data-testid="tenant-edit-button"><Link to={`/admin/cabinets/${id}/modifier`}><Pencil className="h-4 w-4 mr-2" />Modifier</Link></Button>
        </>} />
      {invite && <div className="mb-6"><InviteNotice url={invite.invite_url} email={invite.email} /></div>}
      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tenant-tab-overview">Aperçu</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tenant-tab-modules">Modules</TabsTrigger>
          <TabsTrigger value="domains" data-testid="tenant-tab-domains">Domaines</TabsTrigger>
          <TabsTrigger value="members" data-testid="tenant-tab-members">Utilisateurs</TabsTrigger>
          <TabsTrigger value="backups" data-testid="tenant-tab-backups">Sauvegardes et autorisations</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview t={t} stats={data.stats} /></TabsContent>
        <TabsContent value="modules"><ModulesTab id={id} /></TabsContent>
        <TabsContent value="domains">
          <Panel title="Domaines rattachés" actions={<Button asChild variant="outline" size="sm"><Link to="/admin/domaines">Registre des domaines</Link></Button>}>
            <div className="space-y-3">{data.domains.map((d) => <DomainRow key={d.id} d={d} onChange={reload} />)}</div>
          </Panel>
        </TabsContent>
        <TabsContent value="backups"><div className="space-y-6"><Backups id={id} slug={t.slug} /><Pharmacy id={id} t={t} onDone={reload} /></div></TabsContent>
        <TabsContent value="members">
          <Panel className="overflow-hidden">
            <div className="-m-5 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>E-mail</TableHead><TableHead>Rôle</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
                <TableBody>{data.members.map((m) => (
                  <TableRow key={m.membership_id} data-testid="tenant-member-row"><TableCell className="font-medium">{m.name}</TableCell><TableCell>{m.email}</TableCell><TableCell>{m.role}</TableCell><TableCell><StatusBadge status={m.status} /></TableCell></TableRow>
                ))}</TableBody>
              </Table>
            </div>
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
